import type { CanvasTexture } from 'three';
import { canvasTexture } from '../../render/materials/canvasTexture';
import type { Livery, SideRect, VehicleSpec } from './types';

const PX_PER_M = 110;

export type Facing = 'right' | 'left';

interface Frame {
  x: (u: number) => number;
  y: (v: number) => number;
  s: number;
}

function frameFor(spec: VehicleSpec, facing: Facing): Frame {
  return {
    x: (u) => (facing === 'right' ? u : spec.length - u) * PX_PER_M,
    y: (v) => (spec.height - v) * PX_PER_M,
    s: PX_PER_M,
  };
}

function rectPath(ctx: CanvasRenderingContext2D, f: Frame, r: SideRect, radius = 6): void {
  const x0 = Math.min(f.x(r.u0), f.x(r.u1));
  const w = Math.abs(f.x(r.u1) - f.x(r.u0));
  ctx.beginPath();
  ctx.roundRect(x0, f.y(r.v1), w, (r.v1 - r.v0) * f.s, radius);
}

function paintBody(ctx: CanvasRenderingContext2D, f: Frame, spec: VehicleSpec, livery: Livery): void {
  const shade = ctx.createLinearGradient(0, 0, 0, spec.height * f.s);
  shade.addColorStop(0, livery.body);
  shade.addColorStop(1, livery.body);
  ctx.fillStyle = shade;
  ctx.fillRect(0, 0, spec.length * f.s, spec.height * f.s);
  for (const stripe of livery.stripes ?? []) {
    ctx.fillStyle = stripe.color;
    ctx.fillRect(0, f.y(stripe.v1), spec.length * f.s, (stripe.v1 - stripe.v0) * f.s);
  }
  for (const panel of livery.panels ?? []) {
    ctx.fillStyle = panel.color;
    rectPath(ctx, f, panel, 2);
    ctx.fill();
  }
  // Soft shading: darker along the bottom sill, lighter near the roof.
  const light = ctx.createLinearGradient(0, 0, 0, spec.height * f.s);
  light.addColorStop(0, 'rgba(255,255,255,0.18)');
  light.addColorStop(0.55, 'rgba(255,255,255,0)');
  light.addColorStop(1, 'rgba(0,0,0,0.28)');
  ctx.fillStyle = light;
  ctx.fillRect(0, 0, spec.length * f.s, spec.height * f.s);
}

function paintWindows(ctx: CanvasRenderingContext2D, f: Frame, spec: VehicleSpec): void {
  for (const win of spec.windows) {
    rectPath(ctx, f, win, 8);
    const glass = ctx.createLinearGradient(0, f.y(win.v1), 0, f.y(win.v0));
    glass.addColorStop(0, '#2a3a48');
    glass.addColorStop(1, '#0d141b');
    ctx.fillStyle = glass;
    ctx.fill();
    ctx.lineWidth = 4;
    ctx.strokeStyle = '#1a1a1a';
    ctx.stroke();
    ctx.save();
    ctx.clip();
    ctx.fillStyle = 'rgba(255,255,255,0.14)';
    const x = Math.min(f.x(win.u0), f.x(win.u1));
    ctx.beginPath();
    ctx.moveTo(x + 10, f.y(win.v0));
    ctx.lineTo(x + 34, f.y(win.v1));
    ctx.lineTo(x + 52, f.y(win.v1));
    ctx.lineTo(x + 28, f.y(win.v0));
    ctx.fill();
    ctx.restore();
  }
}

function paintDetails(ctx: CanvasRenderingContext2D, f: Frame, spec: VehicleSpec): void {
  ctx.strokeStyle = 'rgba(0,0,0,0.45)';
  ctx.lineWidth = 3;
  if (spec.door) {
    rectPath(ctx, f, spec.door, 4);
    ctx.stroke();
  }
  const { rear, front, radius } = spec.wheels;
  ctx.fillStyle = '#141414';
  for (const u of [rear, front]) {
    ctx.beginPath();
    ctx.arc(f.x(u), f.y(radius * 0.95), (radius + 0.08) * f.s, Math.PI, 0);
    ctx.fill();
  }
  ctx.fillStyle = '#1b1b1b';
  ctx.fillRect(0, f.y(0.5), spec.length * f.s, 0.14 * f.s);
}

function paintText(ctx: CanvasRenderingContext2D, f: Frame, spec: VehicleSpec, livery: Livery): void {
  if (!livery.sideText) return;
  const bottomWindow = Math.min(...spec.windows.map((w) => w.v0));
  const v = bottomWindow - 0.2;
  ctx.font = `italic 900 ${0.3 * f.s}px Impact, "Arial Black", sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  const x = f.x(spec.length * 0.42);
  const maxWidth = spec.length * f.s * 0.62;
  // Sign-writer style: thick light outline, dark drop shadow, bold colour fill.
  ctx.lineWidth = 10;
  ctx.strokeStyle = 'rgba(0,0,0,0.45)';
  ctx.strokeText(livery.sideText, x + 3, f.y(v) + 3, maxWidth);
  ctx.strokeStyle = '#ffffff';
  ctx.strokeText(livery.sideText, x, f.y(v), maxWidth);
  ctx.fillStyle = livery.sideTextColor ?? '#c1121f';
  ctx.fillText(livery.sideText, x, f.y(v), maxWidth);
}

/** Paints one side of a vehicle. Text is laid out so it reads correctly from either side. */
export function paintSide(spec: VehicleSpec, livery: Livery, facing: Facing): CanvasTexture {
  const f = frameFor(spec, facing);
  const texture = canvasTexture(Math.ceil(spec.length * f.s), Math.ceil(spec.height * f.s), (ctx) => {
    paintBody(ctx, f, spec, livery);
    paintWindows(ctx, f, spec);
    paintDetails(ctx, f, spec);
    paintText(ctx, f, spec, livery);
  });
  texture.repeat.set(1 / spec.length, 1 / spec.height);
  return texture;
}
